<template>
  <div class="ListingsPage">
    <div class="flex">
      <SideFilter
        class="-translate-y-27 has-announcements:-translate-y-35 has-searchbar:md:translate-y-0 transition-transform duration-500 h-[calc(100vh-105px)] has-announcements:h-[calc(100vh-175px)] min-w-1/3 w-1/3 md:w-[290px] md:min-w-[290px] max-sm:hidden sticky overflow-y-scroll bottom-0 md:top-54 has-announcements:md:top-70 grow-0 shrink-0 pr-8 scrollbar-hover"
      />
      <section class="grow min-w-0 sm:ml-8 mt-6 md:mt-4">
        <div
          v-if="userConfig.tradeAccountStatus === 'pending'"
          class="bg-emerald-50 text-zinc-600 py-7 px-8 rounded-2xl mb-10"
        >
          Thanks for submitting your Trade application. Our team will review it and get back to you within 2 business
          days. If you need to place an urgent order, please reach out to us at
          <a :href="`mailto:${appConfig.marketEmail}`">
            {{ appConfig.marketEmail }}
          </a>
          and we’ll do our best to fast-track your application.
        </div>
        <HeaderTitle
          :longtail="longtail"
          :competition="competition"
          :meta="meta"
          :class="{ 'md:hidden': !shouldShowVisualFilters, 'mt-2 mb-5': shouldShowVisualFilters }"
        />
        <div v-if="shouldShowVisualFilters" v-html="existingHtml"></div>
        <div class="flex-row justify-between items-center hidden md:flex">
          <div v-if="shouldShowVisualFilters && !hideArtworksCount" class="text-md text-zinc-950">
            {{ listingsCountLabel }} Artworks
          </div>
          <div class="flex space-x-4">
            <HeaderTitle :meta="meta" v-if="!shouldShowVisualFilters" :longtail="longtail" :competition="competition" />
          </div>
          <div class="flex self-start shrink-0">
            <CategorySelector />
            <PrizeCategorySelector :categories="prizeCategories(true)" />
            <SortBy />
          </div>
        </div>
        <div class="border-t mt-4" :class="{ hidden: shouldShowVisualFilters }"></div>
        <MobileFilter
          class="sm:border-b border-grey-light py-4 sticky"
          :prizeCategories="prizeCategories()"
          v-if="$mq === 'xs'"
        />
        <ListingPagination
          :per-page="per"
          :price-filter-applied="priceFilterApplied"
          :size-filter-applied="sizeFilterApplied"
          :guest-favourites="guestFavourites"
          class="mb-24"
        />
        <CollectionInformation class="mt-6" :filterData="filterData" />
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex"

import { metaMixin } from "@/pages/ListingsPage/mixins/metaMixin"
import UrlFilterBuilder from "@/lib/Listing/UrlFilterBuilder"
import HeaderTitle from "./HeaderTitle.vue"
import SortBy from "./SortBy.vue"
import CategorySelector from "./CategorySelector.vue"
import PrizeCategorySelector from "./PrizeCategorySelector.vue"
import ListingPagination from "./ListingPagination.vue"
import MobileFilter from "./filters/mobile/MobileFilter.vue"
import SideFilter from "./filters/SideFilter.vue"
import CollectionInformation from "./CollectionInformation.vue"
import { allArtPath } from "@/lib/Listing/PathHelpers"
import { getFeedViewPreference } from "@/lib/Listing/FeedViewPreference"

const MAX_DISPLAY_COUNT = 10000

export default {
  name: "ListingsPage",
  mixins: [metaMixin],
  props: ["filterData", "longtail", "competition", "guestFavourites"],
  mounted() {
    this.setUserArtist(this.userConfig.isArtist)
    const filters = UrlFilterBuilder(window.location)
    filters["longtail_id"] = this.longtail.id
    this.setConfig(this.filterData)
    this.initListings(filters)
    window.dispatchEvent(new Event("listings-page-mounted"))

    this.unsubscribeArtworksCount = window.posthog?.onFeatureFlags?.(() => {
      this.hideArtworksCount = window.posthog?.getFeatureFlag("artworks-count-visibility") === "test"
    })
    this.unsubscribeVisualFilters = window.posthog?.onFeatureFlags?.(() => {
      this.hideVisualFilters = window.posthog?.getFeatureFlag("remove-all-art-category-tiles") !== "control"
    })
    this.initFeedViewExperiment()
  },
  methods: {
    prizeCategories(withAllOption = false) {
      const baseOptions = withAllOption ? [{ title: "Prize categories", value: "all" }] : []
      if (this.userConfig.isAdmin && this.competition.slug) return baseOptions.concat(this.prizeCategoriesMap)
      return []
    },
    initFeedViewExperiment() {
      // Mobile browse feed only — desktop is unchanged.
      if (this.$mq !== "xs") return

      const savedView = getFeedViewPreference()
      if (savedView) this.applyDefaultView(savedView)

      // Apply the cohort default only on the first flag evaluation — onFeatureFlags
      // can refire, and re-applying would clobber a manual view toggle made later.
      let applied = false
      this.unsubscribeFeedView = window.posthog?.onFeatureFlags?.(() => {
        if (applied) return
        applied = true
        const cohortDefault = window.posthog?.getFeatureFlag("mobile-feed-default-view")
        if (!savedView && cohortDefault === "list") {
          this.applyDefaultView("list")
        }
      })
    },
    ...mapActions("filter", ["setUserArtist", "initListings", "setConfig", "applyDefaultView"]),
  },
  computed: {
    shouldShowVisualFilters() {
      return !this.meta?.header && allArtPath() && !this.hideVisualFilters
    },
    listingsCountLabel() {
      if (this.loading || this.listingsCount === MAX_DISPLAY_COUNT)
        return `${MAX_DISPLAY_COUNT.toLocaleString("en-EN")}+`

      return this.listingsCount
    },
    prizeCategoriesMap() {
      return this.competition.categories.map((category) => ({ title: category.title, value: category.id }))
    },
    priceFilterApplied() {
      return this.prices.length > 0 || this.priceRange.length > 0
    },
    sizeFilterApplied() {
      return this.sizes.length > 0 || this.width.length > 0 || this.height.length > 0
    },
    ...mapState("filter", {
      per: (state) => state.filters.per,
      listingsCount: (state) => state.listings_count,
      loading: (state) => state.loading,
      prices: (state) => state.filters.prices,
      priceRange: (state) => state.filters.price_range,
      sizes: (state) => state.filters.sizes,
      width: (state) => state.filters.width,
      height: (state) => state.filters.height,
    }),
  },
  beforeDestroy() {
    this.unsubscribeInitListings?.()
    this.unsubscribeArtworksCount?.()
    this.unsubscribeVisualFilters?.()
    this.unsubscribeFeedView?.()
  },
  beforeCreate() {
    const existingContent = document.querySelector("#visual_filters")
    if (existingContent) this.$options.existingHtml = existingContent.outerHTML
  },
  created() {
    document.querySelector("#artworks_skeleton")?.classList.add("hidden")
  },
  data() {
    return {
      existingHtml: this.$options.existingHtml || "",
      hideArtworksCount: false,
      hideVisualFilters: true,
    }
  },
  components: {
    HeaderTitle,
    CategorySelector,
    SortBy,
    SideFilter,
    MobileFilter,
    ListingPagination,
    CollectionInformation,
    PrizeCategorySelector,
  },
}
</script>
